import type { VariantProps } from "@gluestack-ui/utils/nativewind-utils";
import React, { forwardRef, memo } from "react";
import { responsiveType } from "../responsive-type";
import { headingStyle, sizes } from "./styles";
// The native counterpart types props from RN's <Heading>, so callers may pass
// RN-only props (numberOfLines, pointerEvents, onPress). Those must not be
// forwarded to the DOM heading element — React errors on unknown attributes —
// so we translate them to their web equivalents here.
type IHeadingProps = VariantProps<typeof headingStyle> &
  React.ComponentPropsWithoutRef<"h1"> & {
    as?: React.ElementType;
    numberOfLines?: number;
    pointerEvents?: React.CSSProperties["pointerEvents"];
    onPress?: React.MouseEventHandler<HTMLHeadingElement>;
  };

const MappedHeading = memo(
  forwardRef<HTMLHeadingElement, IHeadingProps>(function MappedHeading(
    {
      size,
      className,
      isTruncated,
      bold,
      underline,
      strikeThrough,
      sub,
      italic,
      highlight,
      ...props
    },
    ref
  ) {
    switch (size) {
      case "5xl":
      case "4xl":
      case "3xl":
        return (
          <h1
            className={headingStyle({
              size,
              isTruncated: isTruncated as boolean,
              bold: bold as boolean,
              underline: underline as boolean,
              strikeThrough: strikeThrough as boolean,
              sub: sub as boolean,
              italic: italic as boolean,
              highlight: highlight as boolean,
              class: className,
            })}
            {...props}
            ref={ref}
          />
        );
      case "2xl":
        return (
          <h2
            className={headingStyle({
              size,
              isTruncated: isTruncated as boolean,
              bold: bold as boolean,
              underline: underline as boolean,
              strikeThrough: strikeThrough as boolean,
              sub: sub as boolean,
              italic: italic as boolean,
              highlight: highlight as boolean,
              class: className,
            })}
            {...props}
            ref={ref}
          />
        );
      case "xl":
        return (
          <h3
            className={headingStyle({
              size,
              isTruncated: isTruncated as boolean,
              bold: bold as boolean,
              underline: underline as boolean,
              strikeThrough: strikeThrough as boolean,
              sub: sub as boolean,
              italic: italic as boolean,
              highlight: highlight as boolean,
              class: className,
            })}
            {...props}
            ref={ref}
          />
        );
      case "lg":
        return (
          <h4
            className={headingStyle({
              size,
              isTruncated: isTruncated as boolean,
              bold: bold as boolean,
              underline: underline as boolean,
              strikeThrough: strikeThrough as boolean,
              sub: sub as boolean,
              italic: italic as boolean,
              highlight: highlight as boolean,
              class: className,
            })}
            {...props}
            ref={ref}
          />
        );
      case "md":
        return (
          <h5
            className={headingStyle({
              size,
              isTruncated: isTruncated as boolean,
              bold: bold as boolean,
              underline: underline as boolean,
              strikeThrough: strikeThrough as boolean,
              sub: sub as boolean,
              italic: italic as boolean,
              highlight: highlight as boolean,
              class: className,
            })}
            {...props}
            ref={ref}
          />
        );
      case "sm":
      case "xs":
        return (
          <h6
            className={headingStyle({
              size,
              isTruncated: isTruncated as boolean,
              bold: bold as boolean,
              underline: underline as boolean,
              strikeThrough: strikeThrough as boolean,
              sub: sub as boolean,
              italic: italic as boolean,
              highlight: highlight as boolean,
              class: className,
            })}
            {...props}
            ref={ref}
          />
        );
      default:
        return (
          <h4
            className={headingStyle({
              size: size as unknown as keyof typeof sizes,
              isTruncated: isTruncated as boolean,
              bold: bold as boolean,
              underline: underline as boolean,
              strikeThrough: strikeThrough as boolean,
              sub: sub as boolean,
              italic: italic as boolean,
              highlight: highlight as boolean,
              class: className,
            })}
            {...props}
            ref={ref}
          />
        );
    }
  })
);

const Heading = memo(
  forwardRef<HTMLHeadingElement, IHeadingProps>(function Heading(
    {
      className,
      size = "lg",
      as: AsComp,
      numberOfLines,
      pointerEvents,
      onPress,
      style,
      ...props
    },
    ref
  ) {
    const {
      isTruncated,
      bold,
      underline,
      strikeThrough,
      sub,
      italic,
      highlight,
    } = props;

    const resolvedClassName = responsiveType(className);

    const clampStyle: React.CSSProperties | undefined =
      numberOfLines && numberOfLines > 0
        ? {
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: numberOfLines,
            overflow: "hidden",
          }
        : undefined;

    const mergedStyle: React.CSSProperties = {
      ...style,
      ...(pointerEvents ? { pointerEvents } : null),
      ...clampStyle,
    };

    if (AsComp) {
      return (
        <AsComp
          className={headingStyle({
            size: size as keyof typeof sizes,
            isTruncated: isTruncated as boolean,
            bold: bold as boolean,
            underline: underline as boolean,
            strikeThrough: strikeThrough as boolean,
            sub: sub as boolean,
            italic: italic as boolean,
            highlight: highlight as boolean,
            class: resolvedClassName,
          })}
          onClick={onPress}
          style={mergedStyle}
          {...props}
          ref={ref}
        />
      );
    }

    return (
      <MappedHeading
        className={resolvedClassName}
        size={size}
        ref={ref}
        onClick={onPress}
        style={mergedStyle}
        {...props}
      />
    );
  })
);

Heading.displayName = "Heading";

export { Heading };
